import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Input, SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useAuth } from '../contexts/authContext';
import { artifactClient } from '../lib/connect/clients';
import { formatFileSize } from '../utils/formatFileSize';
function documentTitle(filename) {
    let title = filename;
    try {
        title = decodeURIComponent(title);
    } catch  {
    // Keep filenames containing literal percent signs readable.
    }
    return title.replace(/\.[a-z0-9]{1,8}$/i, '').replace(/[+_]+/g, ' ').replace(/\s+/g, ' ').trim() || filename;
}
export function DocumentLibraryPicker({ open, onOpenChange, onSelect }) {
    const { user } = useAuth();
    const [search, setSearch] = useState('');
    const [selecting, setSelecting] = useState(false);
    const [selectionError, setSelectionError] = useState(null);
    const query = useInfiniteQuery({
        queryKey: [
            'chat-library-documents',
            user?.tenant_id,
            user?.id
        ],
        initialPageParam: 0,
        enabled: open && !!user?.id,
        queryFn: ({ pageParam })=>artifactClient.listArtifacts({
                filter: {
                    ownerUserId: user?.id
                },
                offset: pageParam,
                limit: 50
            }),
        getNextPageParam: (last, pages)=>{
            const loaded = pages.reduce((count, page)=>count + page.artifacts.length, 0);
            return last.artifacts.length > 0 && BigInt(loaded) < last.total ? loaded : undefined;
        },
        retry: false,
        staleTime: 0
    });
    const documents = query.data?.pages.flatMap((page)=>page.artifacts) ?? [];
    const matches = documents.filter((doc)=>`${doc.filename} ${doc.displayTitle || documentTitle(doc.filename)} ${doc.keywords?.join(' ') ?? ''}`.toLowerCase().includes(search.toLowerCase()));
    const choose = async (doc)=>{
        setSelecting(true);
        setSelectionError(null);
        try {
            await onSelect(doc);
            onOpenChange(false);
        } catch (error) {
            setSelectionError(error instanceof Error ? error.message : 'This document could not be attached. Try again.');
        } finally{
            setSelecting(false);
        }
    };
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: (next)=>{
            setSelectionError(null);
            onOpenChange(next);
        },
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            className: "sm:max-w-2xl",
            "data-testid": Testids.assistant.documentLibraryDialog,
            children: [
                /*#__PURE__*/ _jsxs(DialogHeader, {
                    children: [
                        /*#__PURE__*/ _jsx(DialogTitle, {
                            children: "Add from Documents"
                        }),
                        /*#__PURE__*/ _jsx(DialogDescription, {
                            children: "Choose a file you have already uploaded. Its stored source will be attached to this chat."
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Input, {
                    "aria-label": "Search documents",
                    placeholder: "Search loaded documents…",
                    value: search,
                    onChange: (event)=>setSearch(event.target.value),
                    "data-testid": Testids.assistant.documentLibrarySearch
                }),
                query.isLoading && /*#__PURE__*/ _jsx("output", {
                    children: "Loading documents…"
                }),
                query.isError && /*#__PURE__*/ _jsxs("div", {
                    role: "alert",
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            children: "Your documents could not load."
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            onClick: ()=>void query.refetch(),
                            children: "Retry"
                        })
                    ]
                }),
                selectionError && /*#__PURE__*/ _jsx("p", {
                    role: "alert",
                    children: selectionError
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "max-h-80 space-y-2 overflow-y-auto",
                    "aria-busy": selecting,
                    children: [
                        matches.map((doc)=>/*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "secondary",
                                fullWidth: true,
                                "data-testid": Testids.assistant.documentLibraryItem,
                                "data-row-id": doc.artifactId,
                                "aria-label": doc.filename,
                                disabled: selecting,
                                onClick: ()=>void choose(doc),
                                className: "h-auto min-w-0 justify-start whitespace-normal px-4 py-3 text-left",
                                children: /*#__PURE__*/ _jsxs("span", {
                                    className: "min-w-0 w-full space-y-1",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "block whitespace-normal break-words [overflow-wrap:anywhere] leading-snug",
                                            children: doc.displayTitle || documentTitle(doc.filename)
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "block break-words [overflow-wrap:anywhere] text-xs font-normal text-muted-foreground",
                                            children: doc.filename
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            className: "block text-xs font-normal text-muted-foreground",
                                            children: [
                                                doc.filename.match(/\.([a-z0-9]{1,8})$/i)?.[1]?.toUpperCase() ?? 'File',
                                                ' · ',
                                                formatFileSize(Number(doc.sizeBytes)) || '0 Bytes',
                                                ' · ',
                                                doc.createdAt ? `Uploaded ${doc.createdAt.toDate().toLocaleDateString(undefined, {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric'
                                                })}` : 'Upload date unavailable'
                                            ]
                                        })
                                    ]
                                })
                            }, doc.artifactId)),
                        !query.isLoading && !query.isError && matches.length === 0 && /*#__PURE__*/ _jsx("p", {
                            children: documents.length === 0 ? 'No uploaded documents yet. Upload a file to get started.' : 'No matching documents on the loaded pages.'
                        })
                    ]
                }),
                query.hasNextPage && /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "secondary",
                    disabled: query.isFetchingNextPage,
                    onClick: ()=>void query.fetchNextPage(),
                    children: query.isFetchingNextPage ? 'Loading…' : 'Load more documents'
                }),
                selecting && /*#__PURE__*/ _jsx("output", {
                    children: "Attaching document…"
                })
            ]
        })
    });
}
